import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchChecksForGroup, strapiRemoteUri, deleteCheck } from '../strapi/queries';
import { Timeline } from 'primereact/timeline';
import { Card } from 'primereact/card';
import { Chip } from 'primereact/chip';
import { Box } from '@mui/material';
import { Typography, Alert } from '@mui/material';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';

import './TimelineDemo.css'; // This file needs to be created
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';

function getRoleByGroupId(groups, groupId) {
   const entry = groups?.memberships?.find(item => item.group.documentId === groupId);
   return entry ? entry.role : null; // null, falls nicht gefunden
}

export default function CheckReportPage({ memberships, groupChangeKey }) {
   const { activeGroupId } = useParams();
   const [checks, setChecks] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [deleteError, setDeleteError] = useState(null);
   const [showAllItems, setShowAllItems] = useState({}); // State to manage visibility for each check

   const events = checks.map((check, index) => {
      let icon = '';
      let color = '';
      let result = '';

      switch (check.result) {
         case 'success':
            icon = 'pi pi-check';
            color = 'green';
            result = "Bestanden";
            break;
         case 'noresult':
            icon = 'pi pi-question-circle';
            color = 'orange';
            result = "Ohne Ergebnis";
            break;
         case 'fail':
            icon = 'pi pi-exclamation-triangle';
            color = 'red';
            result = "Nicht Bestanden";
            break;
         default:
            icon = 'pi pi-question-circle';
            color = 'gray';
            result = "Unbekannt";
      }

      const role = getRoleByGroupId(memberships, check.group.documentId);

      return {
         id: check.documentId || check.id || index,
         norm: check.standard,
         group_id: check.group.documentId,
         group_role: role,
         date: new Date(check.date).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
         }),
         icon,
         color,
         comment: check.comment,
         items: check.items,
         username: check.user.username,
         result
      };
   });

   const customizedMarker = (item) => {
      return (
         <span className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-1" style={{ backgroundColor: item.color }}>
            <i className={item.icon}></i>
         </span>
      );
   };

   const customizedContent = (item) => {
      const isExpanded = showAllItems[item.id];
      const displayedItems = isExpanded ? item.items : item.items.slice(0, 5);

      const toggleItemsVisibility = () => {
         setShowAllItems(prevState => ({
            ...prevState,
            [item.id]: !prevState[item.id]
         }));
      };

      return (
         <>
            <Card title={item.norm ? item.norm : 'N/A'} subTitle={item.username + " am " + item.date}
               style={{ position: 'relative' }}>
               <div className="p-d-flex p-flex-wrap p-gap-2">
                  <div className="card flex flex-wrap gap-2" style={{ marginTop: "10px" }}>

                     {displayedItems.map((checkItem, index) => {
                        const variant = checkItem.item.variant.find(v => v.color === checkItem.color);
                        const imageUrl = strapiRemoteUri + variant?.pic?.formats?.thumbnail?.url

                        return (
                           <Chip
                              label={
                                 <div>
                                    <div>{checkItem.item.name}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>{checkItem.serial}</div>
                                 </div>
                              }
                              image={imageUrl}
                              className="custom-chip"
                           />
                        )
                     })
                     }
                     {item.items.length > 5 && (
                        <Chip
                           label={isExpanded ? "... weniger" : "mehr ..."}
                           onClick={toggleItemsVisibility}
                           className="custom-chip"
                        />
                     )}
                  </div>
               </div>
               <br></br>
               {item.comment &&
                  <>
                     <Typography sx={{ justifyContent: "left", display: "flex" }} variant="body" color="text.secondary">
                        Kommentar: {item.comment}
                     </Typography>
                     <p></p>
                  </>
               }
               {item.result &&
                  <>
                     <Typography sx={{ justifyContent: "left", display: "flex" }} variant="body" color="text.secondary">
                        Resultat: {item.result}
                     </Typography>
                     <p></p>
                  </>
               }
               {(item.group_role == "admin" || item.group_role == "checkOnly") && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "5px" }}>
                     <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={async () => {
                           if (window.confirm('Sind Sie sicher, dass Sie diesen Check löschen möchten?')) {
                              try {
                                 setDeleteError(null);
                                 await deleteCheck(item);
                                 // Refresh the checks list after successful deletion
                                 const data = await fetchChecksForGroup(activeGroupId);
                                 setChecks(data.data);
                              } catch (err) {
                                 console.error("Fehler beim Löschen des Checks:", err);
                                 setDeleteError(`Fehler beim Löschen des Checks.`);
                              }
                           }
                        }}
                        aria-label="Delete"
                     >
                        Check löschen
                     </Button>
                  </div>
               )}
            </Card>
         </>
      );
   };

   useEffect(() => {
      const getChecks = async () => {
         try {
            const data = await fetchChecksForGroup(activeGroupId);
            setChecks(data.data);
         } catch (err) {
            setError(err);
         } finally {
            setLoading(false);
         }
      };

      if (activeGroupId) {
         getChecks();
      }
   }, [activeGroupId]);

   if (loading) {
      return <Box sx={{ p: 2 }}>Laden...</Box>;
   }

   if (error) {
      return <div>Error: {error.message}</div>;
   }

   const sortedEvents = [...events].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
   );

   return (
      <div className="check-report-page" style={{ marginBottom: "20px" }}>
         <div className="index-page-container">
            <h2>Prüfprotokolle</h2>
            <p>
               Hier findest du eine chronologische Übersicht aller durchgeführten Checks. Klicke auf einen Ausrüstungsgegenstand, um detaillierte Informationen zur Prüfhistorie zu erhalten.            </p>
         </div>
         {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
               {deleteError}
            </Alert>
         )}
         <Timeline value={sortedEvents} align="alternate" className="customized-timeline" marker={customizedMarker} content={customizedContent} />
      </div>
   );
};

